import React from 'react';
import axios from 'axios';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { API_BASE_URL } from '@/config';

interface PerfiosConsentCheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

/**
 * PerfiosConsentCheckbox - Atomic component for Perfios KYC consent
 *
 * This component displays a checkbox for users to consent to Perfios KYC data fetch.
 * It's used in the Caprion onboarding flow to get user permission for fetching
 * PAN, name, address, DOB, and gender from Perfios.
 *
 * When checked/unchecked, saves state to session via API so MobileOTPLogin can read it.
 *
 * @component
 * @example
 * ```tsx
 * <PerfiosConsentCheckbox
 *   checked={consent}
 *   onChange={setConsent}
 *   disabled={loading}
 * />
 * ```
 */
const PerfiosConsentCheckbox: React.FC<PerfiosConsentCheckboxProps> = ({
  checked,
  onChange,
  disabled = false,
}) => {
  const handleChange = async (newChecked: boolean) => {
    // Update local state via onChange prop
    onChange(newChecked);
    
    // Also save to session via API so MobileOTPLogin can access it
    try {
      await axios.post(`${API_BASE_URL}/api/los/set-perfios-consent/`, {
        perfios_consent: newChecked
      });
      console.log('[PerfiosConsentCheckbox] Saved consent to session:', newChecked);
    } catch (error) {
      console.error('[PerfiosConsentCheckbox] Failed to save consent:', error);
      // Don't block the UI if API fails - local state is still updated
    }
  };

  return (
    <div className="flex items-start space-x-3">
      <Checkbox
        id="perfios-consent"
        checked={checked}
        onCheckedChange={handleChange}
        disabled={disabled}
        className="mt-1"
      />
      <div className="flex-1">
        <Label
          htmlFor="perfios-consent"
          className="text-sm font-medium text-gray-900 cursor-pointer"
        >
          I consent to fetch my KYC details from Perfios for faster processing
        </Label>
      </div>
    </div>
  );
};

export default PerfiosConsentCheckbox;
